import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Film } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { ProjectIndexEntry } from '../types';
import { getProjectIndex, deleteProject } from '../engine/projectStorage';
import { createEmptyProject } from '../store/editorStore';
import { useEditorStore } from '../store/editorStore';
import { saveProject } from '../engine/projectStorage';
import { deleteProjectAssets } from '../engine/proxyManager';
import { clearActiveAsset } from '../renderer/rendererBridge';

function formatDuration(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

export function ProjectsDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = t('editor.studioTitle', 'Vidnight Studio');
    loadProjects();
  }, [t]);

  async function loadProjects() {
    const index = await getProjectIndex();
    setProjects(index);
    setLoading(false);
  }

  function handleNewProject() {
    clearActiveAsset();
    const project = createEmptyProject();
    useEditorStore.getState().loadProject(project);
    saveProject(project).catch(console.error);
    navigate(`/studio/${project.id}`);
  }

  async function handleDelete(e: React.MouseEvent, projectId: string) {
    e.stopPropagation();
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;

    // Find all asset IDs for bulk delete (we'd need full project data — skip for now, just delete project)
    await deleteProject(projectId);
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
  }

  function handleOpen(projectId: string) {
    navigate(`/studio/${projectId}`);
  }

  if (loading) {
    return (
      <div
        className="flex items-center justify-center text-[var(--user-page-text)]"
        style={{
          margin: '-1.5rem -2rem',
          height: 'calc(100vh - var(--view-as-offset, 0px))',
          backgroundColor: 'var(--user-page-bg)',
        }}
      >
        <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col overflow-auto text-[var(--user-page-text)]"
      style={{
        margin: '-1.5rem -2rem',
        height: 'calc(100vh - var(--view-as-offset, 0px))',
        backgroundColor: 'var(--user-page-bg)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--user-page-heading)' }}>
            {t('editor.studioTitle', 'Studio')}
          </h1>
          <p className="text-sm opacity-50 mt-0.5">
            {t('editor.projectCount', '{{count}} projects', { count: projects.length })}
          </p>
        </div>
        <button
          onClick={handleNewProject}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:brightness-110 active:scale-[0.97]"
          style={{ background: 'linear-gradient(135deg, var(--user-page-accent) 0%, #5ce0d6 100%)', color: '#000' }}
        >
          <Plus size={16} strokeWidth={2.5} />
          {t('editor.newProject', 'New Project')}
        </button>
      </div>

      {/* Grid */}
      <div className="flex-1 px-8 py-6">
        {projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 opacity-40">
            <Film size={48} strokeWidth={1.5} />
            <p className="text-sm">{t('editor.noProjects', 'No projects yet. Create one to get started.')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            <AnimatePresence mode="popLayout">
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <ProjectCard
                    project={project}
                    onOpen={() => handleOpen(project.id)}
                    onDelete={(e) => handleDelete(e, project.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  onOpen,
  onDelete,
}: {
  project: ProjectIndexEntry;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      className="group relative w-full text-start rounded-xl overflow-hidden bg-[#1A1A1E] border border-white/5 hover:border-white/15 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--user-page-accent)]/50"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-[#2A2A2E] overflow-hidden">
        {project.thumbnailUrl ? (
          <img
            src={project.thumbnailUrl}
            alt={project.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Film size={32} strokeWidth={1.5} className="text-zinc-600" />
          </div>
        )}

        {/* Duration badge */}
        {project.durationMs > 0 && (
          <div className="absolute bottom-2 start-2 px-2 py-0.5 bg-black/60 backdrop-blur-md rounded-md border border-white/10">
            <span className="text-[11px] font-medium text-white tabular-nums tracking-wider">
              {formatDuration(project.durationMs)}
            </span>
          </div>
        )}

        {/* Delete button (hover) */}
        <button
          onClick={onDelete}
          className="absolute top-2 end-2 p-1.5 rounded-lg bg-black/60 text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
          title={t('common.delete', 'Delete')}
        >
          <Trash2 size={14} />
        </button>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors pointer-events-none" />
      </div>

      {/* Info */}
      <div className="px-3 py-2.5">
        <p className="text-sm font-medium truncate text-[var(--user-page-heading)]">
          {project.name}
        </p>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-500">
          <span>{formatRelativeTime(project.updatedAt)}</span>
          <span>·</span>
          <span>{t('editor.trackCount', '{{count}} tracks', { count: project.trackCount })}</span>
        </div>
      </div>
    </div>
  );
}
